// ─────────────────────────────────────────────────────────────
// 학습자 대상(audience) 단일 소스 (taxonomy)
// ─────────────────────────────────────────────────────────────
// 콘솔(kredu)이 `semifab_license` 쿠키의 `audience` 필드에 아래 canonical
// 문자열 중 하나를 실어주면, 반디(AI 튜터)가 답의 "깊이·어휘·비유"를 그 수준에
// 맞춘다. 값의 canonical/라벨/프롬프트 보정 문구는 전부 이 파일 하나에서 온다.
//
// 계약 규칙
//  - canonical 값은 아래 3개 소문자 문자열만: highschool / university / professional
//  - 오타·미상·비문자열은 조용히 무시(null = 미상 → 중립 처리). 콘솔이 잘못 실어도 안 깨짐.
//  - 불변식: 값·측정·골든 목표는 배경과 무관하게 동일(고교생이라고 값 반올림 금지).
//    배경이 바꾸는 것은 오직 "설명 방식"뿐.

export const AUDIENCE = Object.freeze({
  highschool:   { key: 'highschool',   label: '고교',   order: 1 },
  university:   { key: 'university',   label: '대학생', order: 2 },
  professional: { key: 'professional', label: '직장인', order: 3 },
});

// canonical 키 목록(표시 순서 고정)
export const AUDIENCE_KEYS = Object.freeze(['highschool', 'university', 'professional']);

// 콘솔 선택 UI(라디오/드롭다운)용 옵션 — 콘솔 팀이 그대로 import 해서 쓸 수 있게 노출.
export const AUDIENCE_OPTIONS = Object.freeze(
  AUDIENCE_KEYS.map((k) => ({ value: k, label: AUDIENCE[k].label }))
);

/**
 * 원시값 → canonical 키. 오타/미상/비문자열/빈값은 null(조용히 무시).
 * @param {*} raw
 * @returns {('highschool'|'university'|'professional'|null)}
 */
export function normalizeAudience(raw) {
  if (typeof raw !== 'string') return null;
  const k = raw.trim().toLowerCase();
  return AUDIENCE_KEYS.includes(k) ? k : null;
}

/** canonical/원시값 → 사람이 읽는 라벨(고교/대학생/직장인). 미상이면 null. */
export function audienceLabel(raw) {
  const k = normalizeAudience(raw);
  return k ? AUDIENCE[k].label : null;
}

// 반디 프롬프트에 실을 수준별 보정 지침 — "설명 방식"만 바꾼다.
const GUIDANCE = Object.freeze({
  highschool:
    '고등학생 수준. 전문용어는 처음 등장할 때 쉬운 말로 풀고 일상적 비유를 곁들여라. ' +
    '문장은 짧고 명료하게, 수식보다 직관을 먼저 준다. 단, 수치·단위·판정 기준은 그대로 정확히 제시한다.',
  university:
    '이공계 대학생 수준. 기본 물리·화학 개념은 안다고 가정하고 원리와 이유를 연결해 설명하라. ' +
    '적절한 전문용어와 간단한 수식을 쓰되, 유도 과정을 짚어준다.',
  professional:
    '현업 엔지니어 수준. 결론과 핵심 수치를 먼저 제시하고 배경 설명은 최소화하라. ' +
    '현장 용어·약어를 그대로 사용하고, 트레이드오프·예외·실무적 함의를 깊이 있게 다룬다.',
});

/**
 * 수준별 프롬프트 보정 문구. 미상이면 null(중립).
 * @param {*} raw
 * @returns {(string|null)}
 */
export function audienceGuidance(raw) {
  const k = normalizeAudience(raw);
  return k ? GUIDANCE[k] : null;
}
